/* ==========================================================================
   CITYIN3D — Map Book
   A closed book that opens to a single continuous map across both boards.
   Vanilla JS, no dependencies, one instance per section.

   The book is always two boards wide. Opening does not relayout anything:
   the cover rotates on the spine while a camera transform widens the view
   from one board to two. The map's left half is printed on the inside of
   the cover, exactly as a folded plate is in a real book, so the halves
   meet with no seam the moment the cover lies flat.
   ========================================================================== */

(function () {
  'use strict';

  var SVG = {
    open:  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M3 5.5c3-1.2 6-1.2 9 0v14c-3-1.2-6-1.2-9 0zM21 5.5c-3-1.2-6-1.2-9 0v14c3-1.2 6-1.2 9 0z"/></svg>',
    close: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M4 4l16 16M20 4L4 20"/></svg>',
    plus:  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>',
    minus: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M5 12h14"/></svg>',
    reset: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M4 9V4h5M20 15v5h-5M20 9V4h-5M4 15v5h5"/></svg>',
    /* A hand-crank cine camera: two magazines on top, a boxy body, a lens
       barrel and the crank. Drawn to read at 30px, which is the size a marker
       is actually seen at. */
    camera:
      '<svg viewBox="0 0 34 26" aria-hidden="true">' +
      /* Magazines. The cut-outs sit in their own group so they can turn while
         the reel stays put: one transform per reel, nothing else moves. */
      '<circle cx="10.5" cy="7" r="6"/>' +
      '<g class="c3dm-pin__reel c3dm-pin__reel--a">' +
      '<circle class="c3dm-pin__glass" cx="10.5" cy="7" r="1.5"/>' +
      '<circle class="c3dm-pin__glass" cx="10.5" cy="3.7" r="1.25"/>' +
      '<circle class="c3dm-pin__glass" cx="7.64" cy="8.65" r="1.25"/>' +
      '<circle class="c3dm-pin__glass" cx="13.36" cy="8.65" r="1.25"/>' +
      '</g>' +
      '<circle cx="21.5" cy="8" r="4.6"/>' +
      '<g class="c3dm-pin__reel c3dm-pin__reel--b">' +
      '<circle class="c3dm-pin__glass" cx="21.5" cy="8" r="1.15"/>' +
      '<circle class="c3dm-pin__glass" cx="21.5" cy="5.5" r="0.95"/>' +
      '<circle class="c3dm-pin__glass" cx="19.34" cy="9.25" r="0.95"/>' +
      '<circle class="c3dm-pin__glass" cx="23.66" cy="9.25" r="0.95"/>' +
      '</g>' +
      '<rect x="4" y="11.5" width="21" height="10.5" rx="2"/>' +
      '<path d="M25 14.2h4.4a1.1 1.1 0 0 1 1.1 1.1v3a1.1 1.1 0 0 1-1.1 1.1H25z"/>' +
      '<circle class="c3dm-pin__glass" cx="27.7" cy="16.8" r="1.15"/>' +
      '<path d="M4 16.8H2.1a1.1 1.1 0 0 0-1.1 1.1v1.7" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
      '</svg>'
  };

  function clamp(v, a, b) { return v < a ? a : (v > b ? b : v); }
  function smooth(t) { return t * t * (3 - 2 * t); }

  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }

  /* ====================================================================== */

  function MapBook(root) {
    this.root = root;
    var data = root.querySelector('[data-c3dm-config]');
    try { this.cfg = data ? JSON.parse(data.textContent) : null; } catch (e) { this.cfg = null; }
    if (!this.cfg) return;

    var c = this.cfg;
    c.ratio = parseFloat(c.ratio) || 0.72;
    c.duration = parseInt(c.duration, 10) || 1100;
    c.map = c.map || {};
    c.pins = c.pins || [];
    c.ui = c.ui || {};

    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.open = false;
    this.moving = false;
    this.progress = 0;
    this.map = { s: 1, tx: 0, ty: 0, live: false };
    this.card = null;
    this.ready = true;

    this.grab();
    this.buildPins();
    this.measure();
    this.bind();

    root.style.setProperty('--c3dm-dur', c.duration + 'ms');
    requestAnimationFrame(function () { root.classList.add('c3dm--eased'); });

    this.preload();
    if (c.openOn === 'view') this.watchViewport();
  }

  MapBook.prototype.grab = function () {
    var r = this.root;
    this.stage = r.querySelector('[data-c3dm-stage]');
    this.body = r.querySelector('[data-c3dm-body]');
    this.cover = r.querySelector('[data-c3dm-cover]');
    this.faceIn = r.querySelector('[data-c3dm-face="in"]');
    this.halfR = r.querySelector('[data-c3dm-half="right"]');
    this.throwEl = r.querySelector('[data-c3dm-throw]');
    this.mapEl = r.querySelector('[data-c3dm-map]');
    this.mapView = r.querySelector('[data-c3dm-mapview]');
    this.canvas = r.querySelector('[data-c3dm-canvas]');
    this.img = r.querySelector('[data-c3dm-img]');
    this.pinLayer = r.querySelector('[data-c3dm-pins]');
    this.cardLayer = r.querySelector('[data-c3dm-cards]');
    this.btnOpen = r.querySelector('[data-c3dm-open]');
    this.btnClose = r.querySelector('[data-c3dm-close]');
    this.mapUI = r.querySelector('[data-c3dm-mapui]');
    this.liveRegion = r.querySelector('[data-c3dm-live]');
    this.probe = r.querySelector('[data-c3dm-probe]');

    this.ui = r.querySelector('.c3dm__ui');

    if (this.btnClose) this.btnClose.insertBefore(el('span', 'c3dm__ico', SVG.close), this.btnClose.firstChild);
    if (this.btnOpen && this.cfg.openIcon !== false) {
      this.btnOpen.insertBefore(el('span', 'c3dm__ico', SVG.open), this.btnOpen.firstChild);
    }
    /* One button, moved rather than duplicated, so there is never a second
       control for a screen reader to find. */
    if (this.btnOpen && this.cfg.openPlace === 'cover' && this.ui) {
      this.ui.appendChild(this.btnOpen);
      this.root.classList.add('c3dm--btn-on-cover');
    }
  };

  MapBook.prototype.buildPins = function () {
    if (!this.pinLayer) return;
    var self = this;
    var m = this.cfg.marker || {};
    var style = m.style || 'ring';
    this.root.dataset.marker = style;
    this.root.classList.toggle('c3dm--spin', style === 'camera' && m.spin !== false);
    this.pinLayer.textContent = '';
    this.pinEls = [];

    this.cfg.pins.forEach(function (p, i) {
      var b = el('button', 'c3dm-pin');
      b.type = 'button';
      b.style.setProperty('--x', (p.x != null ? p.x : 50) + '%');
      b.style.setProperty('--y', (p.y != null ? p.y : 50) + '%');
      b.style.setProperty('--delay', (i * 0.42) + 's');
      if (p.color) b.style.setProperty('--c3dm-pin-color', p.color);
      b.setAttribute('aria-label', p.title || ('Location ' + (i + 1)));
      b.setAttribute('aria-haspopup', 'dialog');
      b.dataset.pin = String(i);

      if (style === 'drop') {
        /* A pin that stands on the map rather than sitting in it. */
        b.appendChild(el('span', 'c3dm-pin__drop',
          '<svg viewBox="0 0 24 32" aria-hidden="true">' +
          '<path d="M12 31C12 31 22 19.5 22 11.8 22 5.8 17.5 1 12 1S2 5.8 2 11.8C2 19.5 12 31 12 31Z"/>' +
          '<circle class="c3dm-pin__eye" cx="12" cy="11.6" r="3.4"/></svg>'));
      } else if (style === 'camera') {
        /* No ring: the camera stands on its own shadow. */
        b.appendChild(el('span', 'c3dm-pin__cam', SVG.camera));
      } else {
        b.appendChild(el('span', 'c3dm-pin__halo'));
        b.appendChild(el('span', 'c3dm-pin__dot'));
      }

      if (style === 'number') {
        var n = el('span', 'c3dm-pin__num');
        n.textContent = String(i + 1);
        b.appendChild(n);
      }

      if (p.title) {
        var lab = el('span', 'c3dm-pin__label');
        lab.textContent = p.title;
        b.appendChild(lab);
      }
      self.pinLayer.appendChild(b);
      self.pinEls.push(b);
    });
  };

  /* ------------------------------------------------------------- Geometry */

  /* One place writes the frame's height, so the map fit and the desktop hug
     never overwrite one another. */
  MapBook.prototype.setStageHeight = function (px) {
    var v = px ? px + 'px' : '';
    if (this.stage.style.height !== v) this.stage.style.height = v;
    if (this.probe && this.probe.style.height !== v) this.probe.style.height = v;
  };

  MapBook.prototype.stageHeight = function () {
    if (this.probe) {
      var h = this.probe.getBoundingClientRect().height;
      if (h) return h;
    }
    return this.stage.getBoundingClientRect().height;
  };

  MapBook.prototype.measure = function () {
    var r = this.stage.getBoundingClientRect();
    if (!r.width) return;
    this.sw = r.width;

    /* On a phone a two-board spread is small. In single-page mode the camera
       stays on one board when the book opens, so the map arrives at full size
       and the visitor pans across it. */
    this.single = this.cfg.mobileOpen === 'single' &&
      window.innerWidth < (this.cfg.mobileBreakpoint || 750);
    this.root.classList.toggle('c3dm--single', !!this.single);

    /* Room left around the book inside the frame. The drop shadow lives in
       this margin; without it the frame clips the shadow and leaves a line. */
    var pad = clamp(parseFloat(this.cfg.inset), 0, 22) || 8;
    this.inset = 1 - pad / 100;
    this.insetW = 1 - (pad * 0.7) / 100;

    var ratio = this.cfg.ratio;

    /* One page on a phone is one half of the map, so the page takes the shape
       of that half and the frame takes the height the page needs. The whole
       map is then on show at full width with nothing cropped and no bands of
       bare paper, and the chosen book shape goes back to being about the
       closed cover. Bounded so a long map can never take over the screen. */
    var ar = parseFloat(this.cfg.map.aspect);
    if (this.single && ar > 0) {
      ratio = ar / 2;
      var vh = window.innerHeight || 800;
      this.fitH = Math.round(clamp(this.sw / ratio / this.inset, vh * 0.34, vh * 0.94));
    } else {
      this.fitH = 0;
    }
    this.setStageHeight(this.fitH);

    this.sh = this.stageHeight();

    var ch = Math.min(this.sh * this.inset, (this.sw * this.insetW) / ratio);
    this.ch = Math.round(ch);
    this.cw = Math.round(ch * ratio);

    this.root.style.setProperty('--c3dm-cw', this.cw + 'px');
    this.root.style.setProperty('--c3dm-ch', this.ch + 'px');

    var boards = this.single ? 1 : 2;
    this.sClosed = Math.min(1.05, (this.sw * this.inset) / this.cw, (this.sh * this.inset) / this.ch);
    this.sOpen = Math.min(1.05, (this.sw * this.insetW) / (this.cw * boards), (this.sh * this.inset) / this.ch);

    /* With one board on show it fills the width of the screen, whatever the
       phone and whatever book shape is chosen. A tall book has to bleed off the
       top and bottom to manage it, which is allowed up to a point; past that
       the height wins, because a map cut in half is worse than a margin. */
    if (this.single) {
      var fillW = this.sw / this.cw;
      var fitH = this.sh / this.ch;
      this.sOpen = Math.min(Math.max(this.sOpen, fillW), fitH * 1.45);
    }

    /* How far the visible window sits from the middle of the two-board sheet. */
    this.panOffset = this.single ? this.cw / 2 : 0;

    /* Half a closed cover, so a control placed on the cover sits at the same
       point on it whatever size the book comes out. */
    this.root.style.setProperty('--c3dm-page-half', (this.ch * this.sClosed / 2) + 'px');
    this.root.style.setProperty('--c3dm-half-w', (this.cw * this.sOpen / (this.single ? 2 : 1)) + 'px');
    this.root.style.setProperty('--c3dm-half-h', (this.ch * this.sOpen / 2) + 'px');
    this.paint();

    this.root.style.setProperty('--c3dm-cam-closed', this.cam(0));
    this.root.style.setProperty('--c3dm-cam-open', this.cam(1));

    this.paint();
    this.layoutMap();
    this.hug();
    this.placeCard(true);
    this.root.classList.add('c3dm--ready');
  };

  /* Open, the spread is wide and short. On a narrow screen that leaves the
     book swimming in a tall frame, so the frame closes in around it. */
  MapBook.prototype.hug = function () {
    if (!this.stage || this.cfg.ui.hug === false) return;
    var want = this.open && !this.single && this.sOpen < 0.78;
    if (want) {
      var h = Math.round(this.ch * this.sOpen + 96);
      this.stage.style.height = Math.max(300, Math.min(h, Math.round(this.sh))) + 'px';
    } else {
      this.setStageHeight(this.fitH);
    }
  };

  MapBook.prototype.cam = function (p) {
    var e = smooth(clamp(p, 0, 1));
    var s = this.sClosed + (this.sOpen - this.sClosed) * e;
    var half = this.cw / 2;
    var dxOpen = this.single ? half : 0;
    var dx = half + (dxOpen - half) * e;
    return 'scale(' + s.toFixed(4) + ') translateX(' + (-dx).toFixed(2) + 'px)';
  };

  /* The map's box inside the two-board spread, in board pixels. */
  MapBook.prototype.box = function () {
    var W = this.cw * 2, H = this.ch;
    var m = this.cfg.map;
    var ar = parseFloat(m.aspect) || (W / H);
    var w, h;
    if (m.fit === 'cover') {
      if (W / H > ar) { w = W; h = w / ar; } else { h = H; w = h * ar; }
    } else {
      if (W / H > ar) { h = H; w = h * ar; } else { w = W; h = w / ar; }
    }
    return { w: w, h: h, x: (W - w) / 2, y: (H - h) / 2 };
  };

  /* Paint the two printed halves: left on the inside of the cover, right on
     the board beneath it. Same box as the live canvas, so they agree exactly. */
  MapBook.prototype.paint = function () {
    var b = this.box();
    var src = this.cfg.map.src;
    if (!src) return;
    var size = b.w + 'px ' + b.h + 'px';
    /* In single-page mode the live canvas rests centred in the one-board
       window, so the printed halves are offset to match. Without this the map
       shifts sideways the instant the live layer takes over. */
    var o = this.panOffset || 0;
    if (this.faceIn) {
      this.faceIn.style.backgroundImage = 'url("' + src + '")';
      this.faceIn.style.backgroundSize = size;
      this.faceIn.style.backgroundPosition = (b.x + o) + 'px ' + b.y + 'px';
    }
    if (this.halfR) {
      this.halfR.style.backgroundImage = 'url("' + src + '")';
      this.halfR.style.backgroundSize = size;
      this.halfR.style.backgroundPosition = (b.x + o - this.cw) + 'px ' + b.y + 'px';
    }
  };

  /* --------------------------------------------------------- Open / close */

  MapBook.prototype.setProgress = function (p, live) {
    this.progress = clamp(p, 0, 1);
    var deg = -180 * this.progress;
    this.cover.style.transform = 'rotateY(' + deg.toFixed(2) + 'deg)';
    this.body.style.transform = this.cam(this.progress);
    if (this.throwEl) {
      this.throwEl.style.opacity = (Math.sin(this.progress * Math.PI) * 0.85).toFixed(3);
      this.throwEl.style.transform = 'rotateY(' + (deg * 0.45).toFixed(2) + 'deg)';
    }
    /* The inside of the case only exists once the cover has swung past
       upright. Before that, the visitor should see the background. */
    this.root.classList.toggle('c3dm--boards', this.progress > 0.46);
    if (live && this.progress > 0.96) this.goLive(true);
    else if (this.progress < 0.9) this.goLive(false);
  };

  MapBook.prototype.release = function () {
    /* Hand control back to CSS so the settle uses the authored curve. */
    this.cover.style.transform = '';
    this.body.style.transform = '';
    if (this.throwEl) { this.throwEl.style.opacity = ''; this.throwEl.style.transform = ''; }
  };

  MapBook.prototype.toggle = function () { this.setOpen(!this.open); };

  MapBook.prototype.setOpen = function (want) {
    var self = this;
    if (want === this.open && !this.dragging) return;
    this.open = want;
    this.dragging = false;
    this.progress = want ? 1 : 0;

    if (!want) { this.closeCard(true, true); this.goLive(false); this.resetMap(); }

    this.root.classList.add('c3dm--eased', 'c3dm--moving');
    this.release();
    this.hug();

    /* Under an opening cover there is a table, not a page. The case interior
       is put there only once the cover has landed flat on top of it, and taken
       away before it lifts again, so the change happens entirely behind the
       cover and is never seen. Anything revealed mid-swing reads as a stray
       page appearing before the book has opened. */
    /* The raised spine belongs to a closed book. It goes as the cover passes
       upright and comes back on the way down, which is 0.393 of the way
       through the easing curve. Held any longer it sits between the two map
       halves as a dark bar down the middle of the open spread. */
    clearTimeout(this._foldT);
    var self2 = this;
    this._foldT = setTimeout(function () {
      self2.root.classList.toggle('c3dm--fold', want);
    }, Math.round((this.reduced ? 60 : this.cfg.duration) * 0.393));

    clearTimeout(this._boardT);
    if (want) {
      this._boardT = setTimeout(function () {
        self.root.classList.add('c3dm--boards');
      }, this.reduced ? 120 : this.cfg.duration);
    } else {
      this.root.classList.remove('c3dm--boards');
    }
    this.root.classList.toggle('c3dm--open', want);
    if (this.stage) this.stage.setAttribute('aria-expanded', String(want));
    if (this.liveRegion) this.liveRegion.textContent = want ? 'The book is open. The map is shown.' : 'The book is closed.';

    clearTimeout(this._moveT);
    var d = this.reduced ? 160 : this.cfg.duration;
    this._moveT = setTimeout(function () {
      self.root.classList.remove('c3dm--moving');
      if (self.open) self.goLive(true);
    }, d);

    if (want) this.loadMap();
    if (this._kb) {
      this._kb = false;
      var target = want ? this.btnClose : this.btnOpen;
      if (target) setTimeout(function () { try { target.focus({ preventScroll: true }); } catch (e) {} }, d);
    }
  };

  MapBook.prototype.goLive = function (on) {
    if (on === this.map.live) return;
    this.map.live = on;
    this.root.classList.toggle('c3dm--live', on);
    if (this.mapView) this.mapView.style.touchAction = on ? 'none' : 'auto';
    var self = this;
    clearTimeout(this._pinT);
    if (on) {
      this.layoutMap();
      this._pinT = setTimeout(function () { self.root.classList.add('c3dm--pins'); }, 260);
    } else {
      this.root.classList.remove('c3dm--pins');
    }
  };

  MapBook.prototype.watchViewport = function () {
    var self = this;
    if (!('IntersectionObserver' in window)) { this.setOpen(true); return; }
    var io = new IntersectionObserver(function (es) {
      if (es[0] && es[0].isIntersecting) {
        io.disconnect();
        setTimeout(function () { if (!self.open) self.setOpen(true); }, 420);
      }
    }, { threshold: 0.55 });
    io.observe(this.stage);
    this._io = io;
  };

  /* ------------------------------------------------------------- The map */

  MapBook.prototype.layoutMap = function () {
    this.paint();
    var b = this.box();
    this.mw = b.w; this.mh = b.h;
    this.root.style.setProperty('--c3dm-map-w', b.w + 'px');
    this.root.style.setProperty('--c3dm-map-h', b.h + 'px');
    /* Until the visitor moves it, the map rests where the settings put it. */
    if (this.map.s < 1.02) this.map.tx = this.homeX();
    this.clampMap();
    this.applyMap(false);
  };

  MapBook.prototype.loadMap = function () {
    if (!this.img || this.img.dataset.loaded) return;
    var m = this.cfg.map;
    if (!m.src) return;
    this.img.dataset.loaded = '1';
    var img = this.img;
    img.sizes = Math.ceil(this.cw * 2 * Math.min(window.devicePixelRatio || 1, 2.5)) + 'px';
    img.alt = m.alt || '';
    img.addEventListener('load', function () { img.classList.add('is-ready'); }, { once: true });
    if (m.srcset) img.srcset = m.srcset;
    img.src = m.src;
    if (img.complete) img.classList.add('is-ready');
  };

  /* The map is the whole point of the book, so it is fetched before anyone
     asks for it, unless the connection says otherwise. */
  MapBook.prototype.preload = function () {
    var self = this;
    var conn = navigator.connection;
    if (conn && (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || ''))) return;
    var run = function () {
      var m = self.cfg.map;
      if (!m.src) return;
      var i = new Image();
      i.decoding = 'async';
      i.sizes = Math.ceil(self.cw * 2 * Math.min(window.devicePixelRatio || 1, 2)) + 'px';
      if (m.srcset) i.srcset = m.srcset;
      i.src = m.src;
      /* Every location photo, not just the first: a card that waits on its
         image is a card that looks like it stuttered. */
      self._warm = self._warm || [];
      self.cfg.pins.forEach(function (p) {
        if (!p || !p.img) return;
        var j = new Image();
        j.decoding = 'async';
        if (p.imgSrcset) { j.srcset = p.imgSrcset; j.sizes = '(min-width: 750px) 320px, 60vw'; }
        j.src = p.img;
        self._warm.push(j);
      });
    };
    if ('requestIdleCallback' in window) requestIdleCallback(run, { timeout: 3000 });
    else setTimeout(run, 1600);
  };

  MapBook.prototype.applyMap = function (eased) {
    if (!this.canvas) return;
    var s = this.map;
    /* Whatever went wrong upstream, the map is never left with a transform the
       browser has to throw away, which is what makes it stop responding. */
    if (!isFinite(s.s) || !isFinite(s.tx) || !isFinite(s.ty)) {
      s.s = isFinite(s.s) ? clamp(s.s, 1, this.maxZoom()) : 1;
      s.tx = this.homeX();
      s.ty = 0;
      this.clampMap();
    }
    this.canvas.classList.toggle('is-eased', !!eased);
    this.canvas.style.transform =
      'translate(-50%,-50%) translate(' + s.tx.toFixed(1) + 'px,' + s.ty.toFixed(1) + 'px) scale(' + s.s.toFixed(4) + ')';
    this.root.style.setProperty('--c3dm-pin-cs', (1 / s.s).toFixed(4));
    var zoomed = s.s > 1.02;
    this.root.classList.toggle('c3dm--zoomed', zoomed);
    /* While the map is on show, a finger on it belongs to the map: the page
       behind must never scroll out from under it. The book is only part of
       the page, so there is always somewhere else to scroll from. */
    if (this.mapView) this.mapView.style.touchAction = s.live ? 'none' : 'auto';
    if (eased) {
      var self = this;
      clearTimeout(this._easeT);
      this._easeT = setTimeout(function () {
        self.canvas.classList.remove('is-eased');
      }, 440);
    }
  };

  MapBook.prototype.viewW = function () { return this.cw * (this.single ? 1 : 2); };

  /* Where the map rests before anyone touches it. On a phone only one page is
     on show, so which part of the map that is becomes a choice. */
  MapBook.prototype.homeX = function () {
    var o = this.panOffset || 0;
    if (!this.single || !this.mw) return o;
    var maxX = Math.max(0, (this.mw - this.viewW()) / 2);
    var mode = this.cfg.mobileStart || 'center';
    if (mode === 'left') return o + maxX;
    if (mode === 'right') return o - maxX;
    if (mode === 'pin') {
      var p = this.cfg.pins[(parseInt(this.cfg.mobileStartPin, 10) || 1) - 1];
      if (p && p.x != null) return o - (parseFloat(p.x) / 100 - 0.5) * this.mw;
    }
    return o;
  };

  /* How much of a board's height the frame actually shows. When one board is
     bled off the top and bottom to fill the width, this is less than the board,
     and the difference is what the visitor pans through. */
  MapBook.prototype.viewH = function () {
    return Math.min(this.ch, this.sh / (this.sOpen || 1));
  };

  MapBook.prototype.clampMap = function () {
    var s = this.map;
    var o = this.panOffset || 0;
    var maxX = Math.max(0, (this.mw * s.s - this.viewW()) / 2);
    var maxY = Math.max(0, (this.mh * s.s - this.viewH()) / 2);
    s.tx = clamp(s.tx, o - maxX, o + maxX);
    s.ty = clamp(s.ty, -maxY, maxY);
  };

  /* Pannable whenever the map is wider or taller than the window, not only
     when it has been zoomed. */
  MapBook.prototype.pannable = function () {
    return this.mw * this.map.s > this.viewW() + 1 || this.mh * this.map.s > this.viewH() + 1;
  };

  MapBook.prototype.maxZoom = function () {
    var m = this.cfg.map;
    if (m.zoom === false) return 1;
    return parseFloat(m.maxZoom) || 3.2;
  };

  MapBook.prototype.zoomTo = function (scale, cx, cy, eased) {
    var s = this.map;
    var ns = clamp(scale, 1, this.maxZoom());
    if (cx != null) {
      var k = ns / s.s;
      s.tx = cx - (cx - s.tx) * k;
      s.ty = cy - (cy - s.ty) * k;
    }
    s.s = ns;
    this.clampMap();
    if (this.card) this.closeCard(false, true);
    this.applyMap(eased !== false);
  };

  MapBook.prototype.resetMap = function () {
    if (this.card) this.closeCard(false, true);
    this.map.s = 1; this.map.tx = this.homeX(); this.map.ty = 0;
    this.clampMap();
    this.applyMap(true);
  };

  /* ---------------------------------------------------------- Pin cards */

  MapBook.prototype.isSheet = function () {
    var r = this.stage.getBoundingClientRect();
    return r.width < 560 || r.height < 430;
  };

  MapBook.prototype.openPin = function (i) {
    var p = this.cfg.pins[i];
    if (!p) return;
    var self = this;
    this.closeCard(true, true);

    var card = el('div', 'c3dm-card');
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-label', p.title || 'Location');
    card.tabIndex = -1;
    /* Decide the shape before the card is ever laid out. Finding out
       afterwards is what made the photo appear at one size and jump to
       another the first time a pin was tapped. */
    var sheetNow = this.isSheet();
    card.dataset.place = sheetNow ? 'sheet' : 'top';

    var im = null;
    if (p.img) {
      var media = el('div', 'c3dm-card__media');
      im = el('img');
      im.alt = p.imgAlt || p.title || '';
      im.decoding = 'async';
      if (p.imgSrcset) { im.srcset = p.imgSrcset; im.sizes = '(min-width: 750px) 320px, 60vw'; }
      im.addEventListener('load', function () { im.classList.add('is-ready'); }, { once: true });
      im.src = p.img;
      if (im.complete) im.classList.add('is-ready');
      media.appendChild(im);
      card.appendChild(media);
    }

    var body = el('div', 'c3dm-card__body');
    if (p.eyebrow) { var ey = el('p', 'c3dm-card__eyebrow'); ey.textContent = p.eyebrow; body.appendChild(ey); }
    var h = el('h3', 'c3dm-card__title'); h.textContent = p.title || ''; body.appendChild(h);
    if (p.desc) { var d = el('p', 'c3dm-card__text'); d.textContent = p.desc; body.appendChild(d); }
    if (p.extra) { var x = el('p', 'c3dm-card__meta'); x.textContent = p.extra; body.appendChild(x); }
    if (p.url && p.btn) { var a = el('a', 'c3dm-card__cta'); a.href = p.url; a.textContent = p.btn; body.appendChild(a); }
    card.appendChild(body);
    card.appendChild(el('div', 'c3dm-card__arrow'));

    var close = el('button', 'c3dm-card__close', SVG.close);
    close.type = 'button';
    close.setAttribute('aria-label', 'Close');
    close.addEventListener('click', function () { self.closeCard(); self.focusPin(i); });
    card.appendChild(close);

    this.cardLayer.appendChild(card);
    this.card = { node: card, pin: i };
    this.root.classList.add('c3dm--card');
    if (sheetNow) this.fitCard(card);
    if (this.pinEls[i]) this.pinEls[i].classList.add('is-active');

    var mz = this.maxZoom();
    var sheet = this.isSheet();
    if (mz > 1.2) {
      var target = this.single
        ? this.map.s
        : (this.sw < 750 ? Math.min(mz, 1.9) : Math.min(mz, 1.35));
      if (this.single || this.map.s < target - 0.02) {
        var from = { s: this.map.s, tx: this.map.tx, ty: this.map.ty };
        /* Where to put the map back when the card is dismissed. */
        this.card.restore = from;

        /* How far the marker has to rise to clear the card. In page units:
           the lift is applied to the page, which the camera then scales up. */
        var sc0 = this.sOpen || 1;
        var lift = sheet ? (12 + (card.offsetHeight || 180)) / 2 / sc0 : 0;

        /* A map that exactly fills the page has nowhere to rise to, and
           dragging it past its own edge lays bare the paper underneath, which
           is the pale flash either side of the card. So take the room from the
           map instead: go in far enough to make the space, and no further. */
        if (lift > 0) {
          var vh = this.viewH();
          var room = Math.max(0, (this.mh * target - vh) / 2);
          if (room < lift) {
            target = clamp((vh + 2 * lift) / this.mh, target, mz);
            room = Math.max(0, (this.mh * target - vh) / 2);
          }
          lift = Math.min(lift, room);
        }

        var px = (parseFloat(p.x) / 100 - 0.5) * this.mw;
        var py = (parseFloat(p.y) / 100 - 0.5) * this.mh;
        this.map.s = target;
        this.map.tx = -px * target + (this.panOffset || 0);
        this.map.ty = -py * target - lift;
        this.clampMap();

        /* Jump to where the map is going, note where the marker lands, then
           step back and animate. The card is pinned to that landing point from
           the start, so it never chases the marker across the moving map.
           Nothing paints in between: it is all one frame. */
        this.applyMap(false);
        this.card.anchor = this.pinPoint(i);
        var to = { s: this.map.s, tx: this.map.tx, ty: this.map.ty };
        this.map.s = from.s; this.map.tx = from.tx; this.map.ty = from.ty;
        this.applyMap(false);
        this.canvas.getBoundingClientRect();
        this.map.s = to.s; this.map.tx = to.tx; this.map.ty = to.ty;
        this.applyMap(true);
      }
    }

    /* Position and open in the same frame. The card's shape is already fixed
       above, so there is nothing left to wait for: the photo fades into a box
       that is not going to move. */
    var hold = this.card;
    this.placeCard();
    requestAnimationFrame(function () {
      if (self.card !== hold) return;
      card.classList.add('is-open');
      try { card.focus({ preventScroll: true }); } catch (e) {}
    });
  };

  /* The plate stays a plate or it goes. Squeezed into a letterbox by a short
     screen it stops being a photograph of the model, so below that point the
     card drops it and keeps the words. */
  MapBook.prototype.fitCard = function (card) {
    var media = card.querySelector('.c3dm-card__media');
    if (!media) return;
    if (card.dataset.tight) delete card.dataset.tight;
    var r = media.getBoundingClientRect();
    if (r.width && r.height / r.width < 0.7) card.dataset.tight = '1';
  };

  /* Where a marker sits inside the frame, right now. */
  MapBook.prototype.pinPoint = function (i) {
    var pin = this.pinEls[i];
    if (!pin) return null;
    var sr = this.stage.getBoundingClientRect();
    var pr = pin.getBoundingClientRect();
    return { x: pr.left + pr.width / 2 - sr.left, y: pr.top + pr.height / 2 - sr.top };
  };

  MapBook.prototype.placeCard = function (fresh) {
    var ac = this.card;
    if (!ac) return;
    var card = ac.node;
    var sr = this.stage.getBoundingClientRect();

    /* Settled before the marker is measured: a sheet spans the frame and does
       not care where the marker is, and measuring inside the book's 3D subtree
       is the one expensive read on this path. */
    if (sr.width < 560 || sr.height < 430) {
      card.dataset.place = 'sheet';
      card.style.left = ''; card.style.top = '';
      if (fresh) this.fitCard(card);
      return;
    }

    /* The anchor is fixed when the card opens, so the card is placed once and
       stays there. Only a resize takes a fresh reading. */
    if (fresh || !ac.anchor) ac.anchor = this.pinPoint(ac.pin);
    if (!ac.anchor) return;
    var px = ac.anchor.x;
    var py = ac.anchor.y;

    var cw = card.offsetWidth || 320;
    var chh = card.offsetHeight || 300;
    var gap = 18, m = 10;
    var maxL = Math.max(m, sr.width - cw - m);
    var maxT = Math.max(m, sr.height - chh - m);
    var place, left, top;

    /* Above the marker, below it, or beside it. A tall card and a marker near
       the middle leave no room either way, and pinning it above or below then
       means covering the very thing it describes. */
    if (py - chh - gap >= m) {
      place = 'top';
      top = py - chh - gap;
      left = clamp(px - cw / 2, m, maxL);
    } else if (py + gap + chh <= sr.height - m) {
      place = 'bottom';
      top = py + gap;
      left = clamp(px - cw / 2, m, maxL);
    } else {
      var fitsRight = px + gap + cw <= sr.width - m;
      place = (fitsRight || px - gap - cw < m) ? 'right' : 'left';
      left = clamp(place === 'right' ? px + gap : px - gap - cw, m, maxL);
      top = clamp(py - chh / 2, m, maxT);
    }

    card.dataset.place = place;
    card.style.left = Math.round(left) + 'px';
    card.style.top = Math.round(top) + 'px';

    if (place === 'top' || place === 'bottom') {
      var ax = clamp(px - left, 18, cw - 18);
      card.style.setProperty('--ax', Math.round(ax) + 'px');
      card.style.setProperty('--c3dm-card-origin', Math.round(ax) + 'px ' + (place === 'top' ? '100%' : '0%'));
    } else {
      var ay = clamp(py - top, 18, chh - 18);
      card.style.setProperty('--ay', Math.round(ay) + 'px');
      card.style.setProperty('--c3dm-card-origin', (place === 'right' ? '0% ' : '100% ') + Math.round(ay) + 'px');
    }
  };

  /* keepView: the visitor changed the view themselves (panned, zoomed, opened
     another marker), so putting the old one back would fight them. */
  MapBook.prototype.closeCard = function (now, keepView) {
    var ac = this.card;
    if (!ac) return;
    this.card = null;
    if (this.pinEls[ac.pin]) this.pinEls[ac.pin].classList.remove('is-active');
    var n = ac.node;
    this.root.classList.remove('c3dm--card');
    /* Opening a marker leaned the map in towards it. Dismissing the card
       takes that back, so the visitor is returned to the view they had. */
    if (!keepView && ac.restore) {
      this.map.s = ac.restore.s;
      this.map.tx = ac.restore.tx;
      this.map.ty = ac.restore.ty;
      this.clampMap();
      this.applyMap(!now);
    }
    if (now) { n.remove(); return; }
    n.classList.add('is-closing');
    setTimeout(function () { n.remove(); }, 200);
  };

  MapBook.prototype.focusPin = function (i) {
    if (this.pinEls[i]) try { this.pinEls[i].focus({ preventScroll: true }); } catch (e) {}
  };

  /* ------------------------------------------------------------ Bindings */

  MapBook.prototype.bind = function () {
    var self = this;
    var stage = this.stage;

    if (this.btnOpen) this.btnOpen.addEventListener('click', function () { self.setOpen(true); });
    if (this.btnClose) this.btnClose.addEventListener('click', function () { self.setOpen(false); });
    this.cover.addEventListener('click', function (e) {
      if (self.open || self.dragged) return;
      e.preventDefault();
      self.setOpen(true);
    });

    stage.addEventListener('keydown', function (e) {
      if (self.cfg.ui.keyboard === false) return;
      switch (e.key) {
        case 'Enter': case ' ':
          if (!self.open && document.activeElement === stage) { self._kb = true; self.setOpen(true); e.preventDefault(); }
          break;
        case 'Escape':
          if (self.card) { var i = self.card.pin; self.closeCard(); self.focusPin(i); }
          else if (self.open) { self._kb = true; self.setOpen(false); }
          break;
      }
    });

    /* Drag the cover open, or drag it shut again. */
    if (this.cfg.ui.drag !== false) this.bindDrag();

    this.pinLayer.addEventListener('click', function (e) {
      var b = e.target.closest('.c3dm-pin');
      if (!b) return;
      e.stopPropagation();
      if (self._dragged) { self._dragged = false; return; }
      var i = parseInt(b.dataset.pin, 10);
      if (self.card && self.card.pin === i) self.closeCard();
      else self.openPin(i);
    });

    stage.addEventListener('click', function (e) {
      if (e.target.closest('.c3dm-card, .c3dm-pin, button, a')) return;
      if (self.card) self.closeCard();
    });

    this.bindMap();

    if ('ResizeObserver' in window) {
      this._lastW = 0;
      this._ro = new ResizeObserver(function (es) {
        var w = es[0] && es[0].contentRect ? Math.round(es[0].contentRect.width) : 0;
        if (w === self._lastW) return;
        self._lastW = w;
        self.onResize();
      });
      this._ro.observe(stage);
    }
    this._lastVW = window.innerWidth;
    this._lastVH = window.innerHeight;
    this._onWin = function (e) {
      var w = window.innerWidth, h = window.innerHeight;
      var dw = Math.abs(w - self._lastVW), dh = Math.abs(h - self._lastVH);
      self._lastVW = w; self._lastVH = h;
      /* Browser chrome sliding in and out is not a layout change. */
      if (e && e.type === 'resize' && dw === 0 && dh < 140) return;
      self.onResize();
    };
    window.addEventListener('resize', this._onWin);
    window.addEventListener('orientationchange', this._onWin);

    var mm = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mm.addEventListener) mm.addEventListener('change', function (e) { self.reduced = e.matches; });
  };

  MapBook.prototype.bindDrag = function () {
    var self = this;
    var stage = this.stage;
    var g = { on: false, id: null, x0: 0, y0: 0, locked: false, v: 0, lx: 0, lt: 0 };

    stage.addEventListener('pointerdown', function (e) {
      if (e.button > 0 || self.moving) return;
      if (self.map.live && self.pannable()) return;        /* panning the map */
      if (e.target.closest('button, a, .c3dm-card, .c3dm-pin')) return;
      g.on = true; g.locked = false; g.id = e.pointerId;
      g.x0 = g.lx = e.clientX; g.y0 = e.clientY;
      g.lt = performance.now(); g.v = 0;
      self.dragged = false;
    });

    stage.addEventListener('pointermove', function (e) {
      if (!g.on || e.pointerId !== g.id) return;
      var dx = e.clientX - g.x0;
      var dy = e.clientY - g.y0;

      if (!g.locked) {
        if (Math.abs(dx) < 10) return;
        if (Math.abs(dy) > Math.abs(dx) * 1.1) { g.on = false; return; }
        /* Closed books open leftwards; open books shut rightwards. */
        if ((!self.open && dx > 0) || (self.open && dx < 0)) { g.on = false; return; }
        g.locked = true;
        self.dragging = true;
        self.dragged = true;
        self.root.classList.remove('c3dm--eased');
        self.root.classList.add('c3dm--moving');
        try { stage.setPointerCapture(g.id); } catch (err) {}
      }
      if (e.cancelable) e.preventDefault();

      var now = performance.now();
      g.v = g.v * 0.7 + ((e.clientX - g.lx) / Math.max(1, now - g.lt)) * 0.3;
      g.lx = e.clientX; g.lt = now;

      var span = self.cw * self.sClosed * 0.85;
      var p = self.open ? 1 - (dx / span) : (-dx / span);
      self.setProgress(p, true);
    }, { passive: false });

    var up = function (e) {
      if (!g.on || (e && e.pointerId !== g.id)) return;
      g.on = false;
      try { stage.releasePointerCapture(g.id); } catch (err) {}
      if (!g.locked) return;
      var speed = Math.abs(g.v);
      var p = self.progress;
      var want = self.open
        ? !(p < 0.62 || (g.v > 0.5 && p < 0.9))
        : (p > 0.34 || (g.v < -0.5 && p > 0.1));
      self.open = !want;          /* force setOpen to run */
      self.setOpen(want);
      setTimeout(function () { self.dragged = false; }, 30);
    };
    stage.addEventListener('pointerup', up);
    stage.addEventListener('pointercancel', up);
  };

  MapBook.prototype.bindMap = function () {
    var self = this;
    if (!this.mapView) return;
    var pts = new Map();
    var base = null;
    var lastTap = 0, lastTapX = 0, lastTapY = 0;
    var moved = false;
    var axis = null;

    this.mapView.addEventListener('pointerdown', function (e) {
      if (!self.map.live) return;
      /* Nothing to pan and nothing to pinch: leave the pointer to the book so
         a sideways drag still closes it, instead of two handlers fighting. */
      if (!self.pannable() && self.maxZoom() <= 1) return;
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      moved = false;
      axis = null;
      self._dragged = false;
      self.root.classList.add('c3dm--panning');
      if (pts.size === 1) {
        /* A drag that begins on a marker still pans, the way it would on any
           map. The pointer is only captured once the finger has actually
           travelled, so a plain tap still reaches the marker underneath. */
        base = { tx: self.map.tx, ty: self.map.ty, s: self.map.s, x: e.clientX, y: e.clientY };
      } else if (pts.size === 2) {
        self.mapView.style.touchAction = 'none';
        var a = Array.from(pts.values());
        base = {
          tx: self.map.tx, ty: self.map.ty, s: self.map.s,
          d: Math.hypot(a[0].x - a[1].x, a[0].y - a[1].y),
          mx: (a[0].x + a[1].x) / 2, my: (a[0].y + a[1].y) / 2
        };
      }
    });

    this.mapView.addEventListener('pointermove', function (e) {
      if (!pts.has(e.pointerId) || !base) return;
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      var sc = self.sOpen || 1;

      if (pts.size >= 2 && self.maxZoom() > 1 && base.d) {
        var a = Array.from(pts.values());
        var d = Math.hypot(a[0].x - a[1].x, a[0].y - a[1].y);
        var ns = clamp(base.s * (d / base.d), 1, self.maxZoom());
        var k = ns / base.s;
        var r = self.mapView.getBoundingClientRect();
        var cx = (base.mx - r.left - r.width / 2) / sc;
        var cy = (base.my - r.top - r.height / 2) / sc;
        self.map.s = ns;
        self.map.tx = cx - (cx - base.tx) * k;
        self.map.ty = cy - (cy - base.ty) * k;
        self.clampMap();
        self.applyMap(false);
        moved = true;
        axis = 'free';
        self._dragged = true;
        if (e.cancelable) e.preventDefault();
        return;
      }

      var dx = e.clientX - base.x;
      var dy = e.clientY - base.y;
      /* The finger stays on the map whatever direction it travels, so the page
         never jumps because a sideways swipe was a few degrees off. */
      if (e.cancelable) e.preventDefault();
      if (Math.abs(dx) + Math.abs(dy) > 6) moved = true;

      var free = self.map.s > 1.02;
      var panX = self.mw * self.map.s > self.viewW() + 1;
      var panY = self.mh * self.map.s > self.viewH() + 1;

      /* Zoomed in, the finger drags the map in any direction. At rest it is
         locked to one axis after the first few pixels, so a slightly diagonal
         swipe reads as the sideways one it was meant to be. */
      if (free) axis = 'free';
      else if (!axis && Math.abs(dx) + Math.abs(dy) > 8) axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      if (!axis) return;
      if (!self._dragged) {
        self._dragged = true;
        try { self.mapView.setPointerCapture(e.pointerId); } catch (err) {}
      }

      var nx = base.tx, ny = base.ty;
      if (axis === 'free') { nx += dx / sc; ny += dy / sc; }
      else if (axis === 'x') { if (!panX) return; nx += dx / sc; }
      else { if (!panY) return; ny += dy / sc; }

      self.map.tx = nx;
      self.map.ty = ny;
      self.clampMap();
      self.applyMap(false);
    }, { passive: false });

    var up = function (e) {
      if (!pts.has(e.pointerId)) return;
      pts.delete(e.pointerId);
      try { self.mapView.releasePointerCapture(e.pointerId); } catch (err) {}
      if (pts.size) {
        /* One finger of a pinch has left. The gesture becomes a plain drag
           from where the other finger is now: carrying on from the pinch
           anchor, which holds no position, would put the map's offset beyond
           any number and freeze it there. */
        var rest = null;
        pts.forEach(function (v) { if (!rest) rest = v; });
        if (rest) {
          base = { tx: self.map.tx, ty: self.map.ty, s: self.map.s, x: rest.x, y: rest.y };
          axis = self.map.s > 1.02 ? 'free' : null;
        }
        return;
      }
      if (!moved && self.maxZoom() > 1 && self.map.live) {
        var now = performance.now();
        var near = Math.abs(e.clientX - lastTapX) < 44 && Math.abs(e.clientY - lastTapY) < 44;
        if (now - lastTap < 420 && near) {
          var r = self.mapView.getBoundingClientRect();
          var sc = self.sOpen || 1;
          var cx = (e.clientX - r.left - r.width / 2) / sc;
          var cy = (e.clientY - r.top - r.height / 2) / sc;
          if (self.map.s > 1.05) self.resetMap();
          else self.zoomTo(2.1, cx, cy, true);
          lastTap = 0;
        } else { lastTap = now; lastTapX = e.clientX; lastTapY = e.clientY; }
      }
      base = null;
      axis = null;
      self.root.classList.remove('c3dm--panning');
      /* The card belongs to a place on the map. Once the visitor moves the
         map themselves, that place has gone, so the card goes with it. */
      if (moved && self.card) self.closeCard(false, true);
    };
    this.mapView.addEventListener('pointerup', up);
    this.mapView.addEventListener('pointercancel', up);

    this.mapView.addEventListener('wheel', function (e) {
      if (!self.map.live || self.maxZoom() <= 1) return;
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      var r = self.mapView.getBoundingClientRect();
      var sc = self.sOpen || 1;
      var cx = (e.clientX - r.left - r.width / 2) / sc;
      var cy = (e.clientY - r.top - r.height / 2) / sc;
      self.zoomTo(self.map.s * (e.deltaY < 0 ? 1.14 : 0.88), cx, cy, false);
    }, { passive: false });

    if (this.mapUI) {
      this.mapUI.textContent = '';
      if (this.maxZoom() > 1) {
        var mk = function (cls, svg, label, fn) {
          var b = el('button', 'c3dm__mapbtn ' + cls, svg);
          b.type = 'button';
          b.setAttribute('aria-label', label);
          b.addEventListener('click', fn);
          self.mapUI.appendChild(b);
        };
        mk('c3dm__mapbtn--reset', SVG.reset, 'Reset the map', function () { self.resetMap(); });
        mk('c3dm__mapbtn--in', SVG.plus, 'Zoom in', function () { self.zoomTo(self.map.s * 1.5, 0, 0, true); });
        mk('c3dm__mapbtn--out', SVG.minus, 'Zoom out', function () { self.zoomTo(self.map.s / 1.5, 0, 0, true); });
      }
    }
  };

  MapBook.prototype.onResize = function () {
    var self = this;
    clearTimeout(this._rt);
    this._rt = setTimeout(function () { self.measure(); }, 130);
  };

  MapBook.prototype.destroy = function () {
    if (this._ro) this._ro.disconnect();
    if (this._io) this._io.disconnect();
    window.removeEventListener('resize', this._onWin);
    window.removeEventListener('orientationchange', this._onWin);
    this.closeCard(true, true);
    if (this.stage) this.stage.style.height = '';
  };

  /* ====================================================================== */

  var registry = new WeakMap();

  function init(scope) {
    var nodes = (scope || document).querySelectorAll('[data-c3dm-book]');
    Array.prototype.forEach.call(nodes, function (n) {
      if (registry.has(n)) return;
      var inst = new MapBook(n);
      if (!inst.ready) return;
      registry.set(n, inst);
      n.__c3dm = inst;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else { init(); }

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var n = e.target.querySelector('[data-c3dm-book]');
    if (n && n.__c3dm) { n.__c3dm.destroy(); registry.delete(n); }
  });
  document.addEventListener('shopify:block:select', function (e) {
    var t = e.target;
    if (!t || !t.dataset || t.dataset.c3dmBlock !== 'pin') return;
    var host = t.closest('[data-c3dm-book]');
    var inst = host && host.__c3dm;
    if (!inst) return;
    var i = parseInt(t.dataset.c3dmIndex, 10);
    if (isNaN(i)) return;
    if (!inst.open) inst.setOpen(true);
    setTimeout(function () { inst.openPin(i); }, inst.open ? 120 : inst.cfg.duration + 120);
  });

  window.CityIn3DMapBook = { init: init, get: function (n) { return registry.get(n); } };
})();
